/** One of the signed-in person's schedules for a bot: cadence, next run,
 * why it is paused if it is, and the actions that apply. */
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../../shared/ui/button';
import { botTaskSchedulesService, type BotSchedule } from '../../../services/BotTaskSchedulesService';
import type { BotTask } from '../../../services/BotTasksService';
import { deliverableSummary } from './deliverable';
import { canRunNow, formatNextRun, pausedNotice } from './scheduleForm';

interface BotScheduleRowProps {
  schedule: BotSchedule;
  onEdit: (schedule: BotSchedule) => void;
  onChange: (schedule: BotSchedule) => void;
  onDelete: (schedule: BotSchedule) => void;
  onRan: (task: BotTask) => void;
  /** Name the bot above the cadence, for lists that span several bots. */
  showBot?: boolean;
}

export function BotScheduleRow({ schedule, onEdit, onChange, onDelete, onRan, showBot = false }: BotScheduleRowProps) {
  const [busy, setBusy] = useState<'status' | 'run' | null>(null);
  // One id per press that has not yet succeeded: a retry after a lost reply
  // must not start a second task.
  const runId = useRef<string | null>(null);
  const notice = pausedNotice(schedule, false);
  const next = schedule.status === 'active' ? formatNextRun(schedule.nextRunAt, schedule.timezone) : null;

  const setStatus = async (status: 'active' | 'paused') => {
    setBusy('status');
    try { onChange(await botTaskSchedulesService.update(schedule.id, { status })); }
    catch (err) { toast.error(err instanceof Error ? err.message : 'The schedule could not be updated.'); }
    finally { setBusy(null); }
  };
  const runNow = async () => {
    runId.current ??= crypto.randomUUID();
    setBusy('run');
    try {
      onRan(await botTaskSchedulesService.runNow(schedule.id, runId.current));
      runId.current = null;
      toast.success('Started. It shows in the task list.');
    } catch (err) { toast.error(err instanceof Error ? err.message : 'The task could not be started.'); }
    finally { setBusy(null); }
  };

  return (
    <article className="rounded-xl border border-separator bg-card px-4 py-3 font-apple" aria-label={`Schedule: ${schedule.description}`}>
      {showBot && <p className="text-xs font-medium text-muted-foreground">{schedule.botName ?? 'Bot'}</p>}
      <p className="text-sm font-medium break-words">{schedule.description}</p>
      <p className="mt-0.5 text-sm text-muted-foreground line-clamp-2 break-words">{schedule.prompt}</p>
      <p className="mt-1 text-xs text-muted-foreground">
        {next ? `Next run ${next}` : notice ? null : 'No run due'}
        {schedule.deliverTo === 'notification' ? `${next ? ' · ' : ''}Sent as a notification` : ''}
      </p>
      {deliverableSummary(schedule.deliverable) && <p className="mt-0.5 text-xs text-muted-foreground">{deliverableSummary(schedule.deliverable)}</p>}
      {notice && (
        <p className="mt-1 text-xs text-status-warning-foreground">{notice.text}{notice.hint ? `. ${notice.hint}` : ''}</p>
      )}
      <div className="mt-2 flex flex-wrap gap-2">
        {canRunNow(schedule) && <Button size="sm" variant="outline" disabled={busy !== null} aria-label={`Run now, ${schedule.description}`} onClick={() => void runNow()}>{busy === 'run' ? 'Starting' : 'Run now'}</Button>}
        <Button size="sm" variant="outline" disabled={busy !== null} aria-label={`Edit ${schedule.description}`} onClick={() => onEdit(schedule)}>Edit</Button>
        {schedule.status === 'active'
          ? <Button size="sm" variant="ghost" className="text-muted-foreground" disabled={busy !== null} aria-label={`Pause ${schedule.description}`} onClick={() => void setStatus('paused')}>Pause</Button>
          : notice?.canResume && <Button size="sm" variant="ghost" disabled={busy !== null} aria-label={`Resume ${schedule.description}`} onClick={() => void setStatus('active')}>Resume</Button>}
        <Button size="sm" variant="ghost" className="text-destructive" disabled={busy !== null} aria-label={`Delete ${schedule.description}`} onClick={() => onDelete(schedule)}>Delete</Button>
      </div>
    </article>
  );
}
